import { useQueryClient } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import RenderWhen from "../../../../components/RenderWhen";
import Title from "../../../../components/Title";
import { RlCloudSandboxReportDownloadMenu } from "./download";
import { RlCloudSandboxHistory } from "./historytable";
import { useGetMergedRlcsReport } from "./hooks/useGetMergedRlcsReport";
import { useGetSingleRlcsReport } from "./hooks/useGetSingleRlcsReport";
import { useRlcsAnalysisStatus } from "./hooks/useRlcsAnalysisStatus";
import RLCloudSandboxError from "./RLCloudSandboxError";
import RlCloudSandboxSummary from "./RlCloudSandboxSummary";
import RLCloudSandboxTabs from "./RLCloudSandboxTabs";
import RLCloudSandboxToolbar from "./RLCloudSandboxToolbar";
import ViewScreenshotsButton from "./screenshots/ViewScreenshotsButton";
import { AnalysisStatus } from "../../../../lib/enums";
import { ReanalyzeButton } from "../topheader/components";
import { QUOTA_EXCEEDED_MSG } from "./historytable/HistoryTitle";
import AnalyzingLoader from "./historytable/AnalyzingLoader";
import { INITIAL_ANALYSIS_SELECTIONS_WITH_RLCS } from "./utils";

const DEFAULT_REFRESH_RATE = 15 * 1000;

const getTooltipMessageForDisplayStatus = displayStatus =>
  displayStatus === QUOTA_EXCEEDED_MSG
    ? "Daily/Monthly quota has been exceeded."
    : undefined;

const RlCloudSandbox = ({ filename }) => {
  const { url, hash, sourceId, section } = useParams();
  const isCloudDetailsPage = !sourceId;
  const isNetworkReport = Boolean(url);
  const [activeFilter, setActiveFilter] = useState("all");
  const isMergedSelected = !activeFilter || activeFilter === "all";
  const singleReportId = isMergedSelected ? "latest" : activeFilter;

  const queryClient = useQueryClient();

  const {
    mergedReportData,
    isLoadingMerged,
    refetchMergedReport,
    isSuccess: isMergedSuccess,
    error: mergedReportError
  } = useGetMergedRlcsReport(hash, isNetworkReport);
  const {
    data: singleReportData,
    isInitialLoading: singleLoading,
    isFetched: isSingleReportFetched
  } = useGetSingleRlcsReport(hash, singleReportId, isNetworkReport, {
    select: ({ data }) => ({
      ...(isMergedSelected ? {} : data), // enrich merged report with behavioral data only
      behavioral: data.behavioral,
      process_tree: uniqBy(data.process_tree, process => process.process_id)
    }),
    enabled: isMergedSuccess
  });

  const { analysisStatusData, refetchAnalysisStatus, analyisisStatusQueryKey } =
    useRlcsAnalysisStatus({
      hash,
      refetchMergedReportFn: refetchMergedReport,
      pollDisabled: isCloudDetailsPage
    });

  const handleHistoryDropdownChange = value => {
    if (!value) {
      refetchMergedReport();
    }
    setActiveFilter(value);
  };

  const reportData = isMergedSelected
    ? { ...mergedReportData, ...singleReportData } // combine merged data & latest behavioral data
    : singleReportData;

  const {
    general_info: generalInfo,
    history_analysis: historyAnalysis,
    is_stored_report: isStoredReport,
    private_sample: isSamplePrivate
  } = mergedReportData || {};

  const {
    analyzing,
    display_status: displayStatus,
    analysis_status: analysisStatus
  } = analysisStatusData;

  const errorDisplayStatus = displayStatus;
  const latestAnalysisId = historyAnalysis?.[0]?.analysis_id;
  const showScreenshotsThumbNail =
    !isSamplePrivate && (!isStoredReport || isNetworkReport);

  if (isLoadingMerged) {
    return <LoadingOverlay />;
  }

  const tabsContent =
    isLoadingMerged || singleLoading ? (
      <LoadingOverlay />
    ) : (
      <RLCloudSandboxTabs
        hash={hash}
        sourceId={sourceId}
        activeSection={section}
        activeReport={activeFilter}
        data={reportData}
        isStoredReport={isStoredReport}
        isPrivate={isSamplePrivate}
        isNetworkReport={isNetworkReport}
        historyAnalysis={historyAnalysis}
        isMergedSelected={isMergedSelected}
        isLatestFetched={isSingleReportFetched && singleReportId === "latest"}
      />
    );

  return (
    <>
      <div className="rlbs rl-cloud-sandbox">
        <div className={"d-flex flex-row justify-content-between mb-20"}>
          <div>
            <Title
              Level="h1"
              text="Cloud Sandbox Analysis Summary"
              icon="rl-logo-square"
              contentBottom={
                <RenderWhen condition={isStoredReport}>
                  <div className="bold">
                    <span>
                      Warning! Encountered issues while fetching analysis data
                      from the cloud. Showing locally stored data.
                    </span>
                  </div>
                </RenderWhen>
              }
              className={"mb-0"}
            />
          </div>
          <RenderWhen condition={!mergedReportError && !isCloudDetailsPage}>
            <div className={"d-flex gap-10"}>
              <RenderWhen condition={analyzing}>
                <AnalyzingLoader analysisStatus={displayStatus} />
              </RenderWhen>

              <ReanalyzeButton
                initialAnalysisSelections={
                  INITIAL_ANALYSIS_SELECTIONS_WITH_RLCS
                }
                disabled={displayStatus === QUOTA_EXCEEDED_MSG || analyzing}
                tooltipMessage={getTooltipMessageForDisplayStatus(
                  displayStatus
                )}
                onReanalyze={() => {
                  queryClient.setQueryData(analyisisStatusQueryKey, {
                    analyzing: true,
                    analysis_status: "Fetching analysis data...",
                    display_status: AnalysisStatus.QUEUED
                  });
                  setTimeout(() => {
                    refetchAnalysisStatus();
                  }, DEFAULT_REFRESH_RATE); // analysis status takes some time to update
                }}
                shouldRefetchRlcsAnalysisStatus={false}
              />

              <RlCloudSandboxReportDownloadMenu
                analysisId={latestAnalysisId}
                hash={hash}
                isStoredReport={isStoredReport}
                isPrivate={isSamplePrivate}
                isNetworkReport={isNetworkReport}
              />
            </div>
          </RenderWhen>
        </div>
        {mergedReportError ? (
          <RLCloudSandboxError
            dataError={mergedReportError?.data?.message}
            isAnalyzing={analyzing}
            displayStatus={errorDisplayStatus}
          />
        ) : (
          <>
            <RlCloudSandboxSummary
              filename={filename || "--"}
              data={generalInfo}
              screenshotsThumbnailComp={
                <RenderWhen condition={showScreenshotsThumbNail}>
                  <ViewScreenshotsButton
                    hash={hash}
                    historyAnalysis={historyAnalysis}
                    isNetwork={isNetworkReport}
                  />
                </RenderWhen>
              }
            />

            <RlCloudSandboxHistory
              data={historyAnalysis}
              hash={hash}
              isStoredReport={isStoredReport}
              isNetworkReport={isNetworkReport}
              onReportClick={handleHistoryDropdownChange}
            />
            <RLCloudSandboxToolbar
              historyAnalysis={historyAnalysis}
              activeFilter={activeFilter}
              handleHistoryDropdownChange={handleHistoryDropdownChange}
              isStoredReport={isStoredReport}
            />

            {tabsContent}
          </>
        )}
      </div>
    </>
  );
};

RlCloudSandbox.propTypes = {
  filename: PropTypes.string
};

export default RlCloudSandbox;
